'use client';

import Image from 'next/image';
import { ReactNode, useCallback } from 'react';
import { SocialProviders } from 'types/enums';
import styles from './style.module.scss';
import Button from 'components/ui-new/Button';
import classNames from 'classnames';
import { redirect } from 'next/navigation';
import getSocialAuthCallbackUrl from 'features/auth/lib/socialAuthCallbackUrl';
import Gap from 'components/ui-new/Gap';
import { Colors } from 'components/ui-new/Typography/color';
import T from 'components/ui-new/Typography';
import { useTranslations } from 'next-intl';
import { useSocialProviders } from 'features/auth/hooks/useSocialProviders';

type SocialType = {
  key: string;
  icon: ReactNode;
};

const socials: Record<string, SocialType> = {
  [SocialProviders.google]: {
    key: 'google',
    icon: (
      <Image key='google' src='/icons/social-logo/google.svg' width={20} height={20} alt='google' />
    ),
  },
  [SocialProviders.yandex]: {
    key: 'yandex',
    icon: (
      <Image key='yandex' src='/icons/social-logo/yandex.svg' width={20} height={20} alt='yandex' />
    ),
  },
  [SocialProviders.vk]: {
    key: 'vk',
    icon: <Image key='vk' src='/icons/social-logo/vk.svg' width={20} height={20} alt='vk' />,
  },
  [SocialProviders.facebook]: {
    key: 'facebook',
    icon: (
      <Image
        key='facebook'
        src='/icons/social-logo/facebook.svg'
        width={20}
        height={20}
        alt='google'
      />
    ),
  },
  [SocialProviders.twitter]: {
    key: 'twitter',
    icon: (
      <Image
        key='twitter'
        src='/icons/social-logo/twitter.svg'
        width={20}
        height={20}
        alt='twitter'
      />
    ),
  },
};

type Props = {
  isModeration?: boolean;
};

const SocialAuth = ({ isModeration }: Props) => {
  const handleClick = useCallback((social: string) => {
    redirect(
      process.env.HOST + '/api/auth/social/' + social + '?' + getSocialAuthCallbackUrl(social),
    );
  }, []);

  const t = useTranslations();
  const { data: allowedSocials = [], isLoading } = useSocialProviders(isModeration);

  if (isLoading || allowedSocials.length === 0) {
    return null;
  }

  return (
    <>
      {allowedSocials.length > 0 && (
        <>
          <T.Text.BodyShort alignCenter color={Colors.textSubtitle}>
            {t('or')}
          </T.Text.BodyShort>
          <Gap gap={16} />
        </>
      )}
      <div className={styles.container}>
        <div className={styles.socialBlock}>
          {allowedSocials
            .map((s) => socials[s])
            .map((el, i) => (
              <Button
                variant='grey'
                key={i}
                className={classNames(styles.social)}
                onClick={() => handleClick(el.key)}>
                {el.icon}
              </Button>
            ))}
        </div>
      </div>
    </>
  );
};

export default SocialAuth;
